import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  ChevronLeft, Save, Loader2, Plus, AlertCircle, Upload, Sparkles,
  ShieldAlert, Activity, Eye, History, GitCompareArrows,
} from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { AdminAPI } from '@/lib/admin-api';
import { AdminAPI as AdminAPIQ } from '@/lib/admin-api-quality';
import { RuleBuilder } from '@/components/admin/RuleBuilder';
import { RuleSimulator } from '@/components/admin/RuleSimulator';
import { RuleVersions } from '@/components/admin/RuleVersions';
import { ConflictPanel } from '@/components/admin/ConflictPanel';
import { TraceViewer } from '@/components/admin/TraceViewer';
import { ParityPanel } from '@/components/admin/ParityPanel';
import { PrePublishDialog } from '@/components/admin/PrePublishDialog';
import { toast } from 'sonner';

/**
 * AdminCardRules page — restructured (May 2026 / Phase 23.E.6) from a
 * three-column layout into two top-level tabs:
 *
 *   ┌─ Rules & Simulator (default) ───────────────────────────────────────┐
 *   │   Left  ── Rule Builder (compact list rows → drawer for full edit) │
 *   │   Right ── Simulator + Trace + Parity + Conflicts (sub-tabs)        │
 *   └─────────────────────────────────────────────────────────────────────┘
 *   ┌─ Versions & Diff ──────────────────────────────────────────────────┐
 *   │   Full-width RuleVersions panel (active/draft/archived + diff)      │
 *   └─────────────────────────────────────────────────────────────────────┘
 *
 * Why two tabs (and not three): "Rules" and "Simulator" are tightly
 * coupled in workflow — admins tweak a rule and immediately re-run the
 * simulator. Splitting them would force a tab switch on every iteration.
 * Versions and Diff, by contrast, are consulted occasionally (forking,
 * rollback, comparing v5 to v6) and benefit from their own full-width
 * surface for a cleaner diff view.
 *
 * The page header retains the version pills + Save/Publish CTAs so admins
 * never lose sight of which version they're editing, regardless of the
 * active tab.
 */
export default function AdminCardRules() {
  const { id } = useParams();
  const [card, setCard] = useState(null);
  const [versions, setVersions] = useState([]);
  const [currentId, setCurrentId] = useState(null);
  const [current, setCurrent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [creatingDraft, setCreatingDraft] = useState(false);
  const [conflicts, setConflicts] = useState(null);
  const [trace, setTrace] = useState(null);
  const [tracing, setTracing] = useState(false);
  const [showPublishDialog, setShowPublishDialog] = useState(false);
  const [topTab, setTopTab] = useState('rules');

  const loadConflicts = useCallback(async (vid) => {
    if (!vid) return;
    try {
      const c = await AdminAPIQ.quality.conflicts(vid);
      setConflicts(c);
    } catch (_) { setConflicts(null); }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [c, vs] = await Promise.all([
        AdminAPI.cards.get(id),
        AdminAPI.rules.versions(id),
      ]);
      setCard(c);
      setVersions(vs || []);
      if (vs?.length) {
        const stillThere = currentId && vs.find((v) => v.id === currentId);
        const draft = vs.find((v) => v.status === 'draft');
        const active = vs.find((v) => v.status === 'active');
        const pick = stillThere || draft || active || vs[0];
        setCurrentId(pick.id);
        setCurrent({
          rules: pick.rules || [],
          milestones: pick.milestones || [],
          tiers: pick.tiers || [],
          cap_pools: pick.cap_pools || [],
        });
        setDirty(false);
      }
    } catch (e) {
      setError(e.message || 'Load failed');
    } finally {
      setLoading(false);
    }
  }, [id]); // eslint-disable-line

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (currentId) loadConflicts(currentId); }, [currentId, loadConflicts]);

  const currentVersion = useMemo(() => versions.find((v) => v.id === currentId) || null, [versions, currentId]);
  const isDraft = currentVersion?.status === 'draft';
  const locked = !isDraft;

  const onSelect = async (vid) => {
    if (dirty && !window.confirm('Discard unsaved draft changes?')) return;
    setCurrentId(vid);
    const v = versions.find((x) => x.id === vid);
    if (v) {
      setCurrent({
        rules: v.rules || [],
        milestones: v.milestones || [],
        tiers: v.tiers || [],
        cap_pools: v.cap_pools || [],
      });
      setDirty(false);
    }
  };

  const onChange = (next) => {
    setCurrent(next);
    setDirty(true);
  };

  const saveDraft = async () => {
    if (!currentId || !isDraft) return;
    setSaving(true);
    try {
      await AdminAPI.rules.patchDraft(currentId, {
        rules: current.rules,
        milestones: current.milestones,
        tiers: current.tiers || [],
        cap_pools: current.cap_pools || [],
      });
      toast.success('Draft saved');
      await load();
    } catch (e) {
      toast.error(e.message || 'Save failed');
    } finally { setSaving(false); }
  };

  const publishCurrent = () => {
    if (!currentId) return;
    if (dirty) {
      toast.error('Save the draft before publishing');
      return;
    }
    setShowPublishDialog(true);
  };

  const confirmPublish = async (overrideReason) => {
    if (!currentId) return;
    setPublishing(true);
    try {
      await AdminAPI.rules.publish(currentId, overrideReason ? { override_reason: overrideReason } : {});
      toast.success(overrideReason ? 'Published with override. Reason recorded in audit log.' : 'Published. Consumer recommendations now use this version.');
      setShowPublishDialog(false);
      await load();
    } catch (e) {
      toast.error(e.message || 'Publish failed');
    } finally { setPublishing(false); }
  };

  const runTrace = async (form) => {
    if (!currentId) return;
    setTracing(true);
    try {
      const t = await AdminAPIQ.quality.trace({
        card_id: id,
        version_id: currentId,
        amount: Number(form.amount) || 0,
        merchant: form.merchant || undefined,
        mcc: form.mcc || undefined,
        category: form.category || undefined,
        mode: form.mode || 'online',
        international: !!form.international,
        period_used_inr: Number(form.period_used_inr) || 0,
        period_used_points: Number(form.period_used_points) || 0,
        milestone_progress_inr: Number(form.milestone_progress_inr) || 0,
      });
      setTrace(t);
    } catch (e) {
      toast.error(e.message || 'Trace failed');
    } finally { setTracing(false); }
  };

  const createDraft = async () => {
    setCreatingDraft(true);
    try {
      const d = await AdminAPI.rules.createDraft(id);
      toast.success(`New draft v${d.version} created`);
      setCurrentId(d.id);
      await load();
    } catch (e) {
      toast.error(e.message || 'Create draft failed');
    } finally { setCreatingDraft(false); }
  };

  if (loading) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-8 w-72" />
        <Skeleton className="h-10 w-72" />
        <div className="grid lg:grid-cols-2 gap-3">
          <Skeleton className="h-[500px] w-full" />
          <Skeleton className="h-[500px] w-full" />
        </div>
      </div>
    );
  }
  if (error || !card) {
    return (
      <Card className="p-4 border-rose-500/30 bg-rose-500/5 text-rose-700 dark:text-rose-300" data-testid="admin-error-alert">
        <AlertCircle className="h-4 w-4 inline mr-2" />{error || 'Card not found'}
      </Card>
    );
  }

  const activeV = versions.find((v) => v.status === 'active');
  const conflictCount = (conflicts?.counts?.critical || 0) + (conflicts?.counts?.high || 0);

  return (
    <div className="space-y-3" data-testid="admin-card-rules-page">
      {/* Header */}
      <div className="flex items-start sm:items-center justify-between flex-col sm:flex-row gap-3">
        <div className="min-w-0 w-full sm:w-auto">
          <Button asChild size="sm" variant="ghost" className="-ml-2 gap-1.5 text-muted-foreground">
            <Link to={`/admin/cards/${card.id}`}><ChevronLeft className="h-3.5 w-3.5" /> Card</Link>
          </Button>
          <h1 className="font-display text-lg sm:text-xl font-semibold tracking-tight mt-1 truncate">
            {card.bank} — {card.name} <span className="text-muted-foreground font-normal hidden sm:inline">· Reward rules</span>
          </h1>
          <div className="text-[11px] text-muted-foreground mt-0.5">
            Active: {activeV ? `v${activeV.version}` : '—'} · Editing: {currentVersion ? `v${currentVersion.version} · ${currentVersion.status}` : '—'}
          </div>
        </div>
        <div className="flex flex-wrap gap-2 w-full sm:w-auto">
          <Button size="sm" variant="outline" className="gap-1.5 flex-1 sm:flex-none" onClick={createDraft} disabled={creatingDraft} data-testid="admin-card-rules-new-draft">
            {creatingDraft ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
            <span className="hidden xs:inline">New </span>draft
          </Button>
          {isDraft && (
            <Button size="sm" variant={dirty ? 'default' : 'outline'} className="gap-1.5 flex-1 sm:flex-none" onClick={saveDraft} disabled={saving || !dirty} data-testid="admin-card-rules-save-draft">
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
              Save<span className="hidden xs:inline"> draft</span>
            </Button>
          )}
          {isDraft && (
            <Button size="sm" className="gap-1.5 flex-1 sm:flex-none" onClick={publishCurrent} disabled={publishing || dirty} data-testid="admin-card-rules-publish">
              {publishing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
              Publish
            </Button>
          )}
        </div>
      </div>

      {dirty && (
        <Card className="p-2 border-amber-500/40 bg-amber-500/5 flex items-center gap-2 text-xs text-amber-700 dark:text-amber-400">
          <AlertCircle className="h-3.5 w-3.5" />
          You have unsaved changes in this draft. Save before publishing.
        </Card>
      )}

      {/* Top-level tabs — Rules+Simulator | Versions & Diff */}
      <Tabs value={topTab} onValueChange={setTopTab}>
        <TabsList className="w-full sm:w-auto justify-start">
          <TabsTrigger value="rules" data-testid="admin-card-rules-top-tab-rules" className="gap-1.5">
            <Sparkles className="h-3.5 w-3.5" /> Rules &amp; Simulator
          </TabsTrigger>
          <TabsTrigger value="versions" data-testid="admin-card-rules-top-tab-versions" className="gap-1.5">
            <History className="h-3.5 w-3.5" /> Versions &amp; Diff
            <Badge variant="outline" className="ml-1 text-[9px] h-4 px-1">{versions.length}</Badge>
          </TabsTrigger>
        </TabsList>

        {/* TAB 1 — Rules & Simulator side by side */}
        <TabsContent value="rules" className="mt-3">
          <div className="grid lg:grid-cols-[minmax(420px,1.15fr)_minmax(360px,1fr)] gap-3">
            <Card className="p-3" data-testid="admin-card-rules-builder-pane">
              <RuleBuilder
                value={current}
                onChange={onChange}
                locked={locked}
                status={currentVersion?.status || 'draft'}
                cardDefaults={{
                  currency: card?.currency || 'RP',
                  point_value_inr: card?.point_value_inr ?? 1,
                }}
              />
            </Card>
            <Card className="p-3" data-testid="admin-card-rules-sim-pane">
              <Tabs defaultValue="simulator">
                <TabsList className="w-full justify-start flex-wrap h-auto">
                  <TabsTrigger value="simulator"  data-testid="admin-rules-tab-simulator"><Sparkles className="h-3 w-3 mr-1" /> Simulator</TabsTrigger>
                  <TabsTrigger value="trace"      data-testid="admin-rules-tab-trace"><Eye className="h-3 w-3 mr-1" /> Trace</TabsTrigger>
                  <TabsTrigger value="parity"     data-testid="admin-rules-tab-parity"><Activity className="h-3 w-3 mr-1" /> Parity</TabsTrigger>
                  <TabsTrigger value="conflicts"  data-testid="admin-rules-tab-conflicts">
                    <ShieldAlert className="h-3 w-3 mr-1" /> Conflicts
                    {conflictCount > 0 && <Badge variant="outline" className="ml-1 text-[9px] bg-amber-500/10 text-amber-600 border-amber-500/30 h-4 px-1">{conflictCount}</Badge>}
                  </TabsTrigger>
                </TabsList>
                <TabsContent value="simulator" className="mt-3">
                  <RuleSimulator
                    cardId={card.id}
                    versionId={currentId}
                    versionStatus={currentVersion?.status}
                    cardRules={current?.rules || []}
                  />
                </TabsContent>
                <TabsContent value="trace" className="mt-3">
                  <TraceForm onRun={runTrace} running={tracing} />
                  <div className="mt-3">
                    <TraceViewer trace={trace?.trace} breakdown={trace?.breakdown} />
                  </div>
                </TabsContent>
                <TabsContent value="parity" className="mt-3">
                  <ParityPanel cardId={card.id} versionId={currentId} />
                </TabsContent>
                <TabsContent value="conflicts" className="mt-3">
                  <ConflictPanel counts={conflicts?.counts} findings={conflicts?.findings || []} onJumpToRule={(rid) => {
                    const el = document.querySelector(`[data-testid="admin-rule-builder-block-${rid}"]`);
                    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                  }} />
                </TabsContent>
              </Tabs>
            </Card>
          </div>
        </TabsContent>

        {/* TAB 2 — Versions & Diff (full width) */}
        <TabsContent value="versions" className="mt-3">
          <Card className="p-3" data-testid="admin-card-rules-versions-pane">
            <div className="flex items-center gap-2 mb-3">
              <GitCompareArrows className="h-4 w-4 text-accent" />
              <div className="text-sm font-semibold">Version history &amp; diff</div>
              <div className="ml-auto text-[10px] text-muted-foreground hidden sm:block">
                Fork drafts · compare versions · rollback to a known-good state
              </div>
            </div>
            <RuleVersions
              cardId={card.id}
              versions={versions}
              currentVersionId={currentId}
              activeVersionId={activeV?.id}
              onSelectVersion={onSelect}
              onChange={load}
            />
          </Card>
        </TabsContent>
      </Tabs>

      <PrePublishDialog
        open={showPublishDialog}
        onOpenChange={setShowPublishDialog}
        cardId={card.id}
        versionId={currentId}
        onConfirm={confirmPublish}
        publishing={publishing}
      />
    </div>
  );
}


// ──────────────────────────────────────────────────────────────────────
// Trace form (unchanged from previous version)
// ──────────────────────────────────────────────────────────────────────
const TraceForm = ({ onRun, running }) => {
  const [form, setForm] = useState({
    amount: 2000, merchant: 'swiggy', mcc: '5814', category: 'Dining',
    mode: 'online', international: false,
    period_used_inr: 0, period_used_points: 0, milestone_progress_inr: 0,
  });
  const [showAdvanced, setShowAdvanced] = useState(false);
  return (
    <div className="rounded-md border border-border bg-[hsl(var(--surface-2))] p-2 space-y-2">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <TraceInput label="Amount" value={form.amount} onChange={(v) => setForm((f) => ({ ...f, amount: v }))} mono testid="admin-trace-form-amount" />
        <TraceInput label="Merchant" value={form.merchant} onChange={(v) => setForm((f) => ({ ...f, merchant: v }))} testid="admin-trace-form-merchant" />
        <TraceInput label="MCC" value={form.mcc} onChange={(v) => setForm((f) => ({ ...f, mcc: v }))} mono testid="admin-trace-form-mcc" />
        <TraceInput label="Category" value={form.category} onChange={(v) => setForm((f) => ({ ...f, category: v }))} testid="admin-trace-form-category" />
      </div>
      <button
        type="button"
        onClick={() => setShowAdvanced((v) => !v)}
        className="w-full text-left text-[10px] uppercase tracking-wider text-muted-foreground hover:text-foreground pt-1 border-t border-border"
        data-testid="admin-trace-form-advanced-toggle"
      >
        {showAdvanced ? '− Hide' : '+ Show'} advanced (period usage, milestone progress)
      </button>
      {showAdvanced && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          <TraceInput label="Period used (₹)" value={form.period_used_inr} onChange={(v) => setForm((f) => ({ ...f, period_used_inr: v }))} mono testid="admin-trace-form-period-used" />
          <TraceInput label="Points used" value={form.period_used_points} onChange={(v) => setForm((f) => ({ ...f, period_used_points: v }))} mono testid="admin-trace-form-period-used-points" />
          <TraceInput label="Milestone ₹ so far" value={form.milestone_progress_inr} onChange={(v) => setForm((f) => ({ ...f, milestone_progress_inr: v }))} mono testid="admin-trace-form-milestone-progress" />
        </div>
      )}
      <div className="flex items-center justify-end">
        <Button size="sm" onClick={() => onRun(form)} disabled={running} className="gap-1.5" data-testid="admin-trace-run">
          {running ? <Loader2 className="h-3 w-3 animate-spin" /> : <Eye className="h-3 w-3" />} Trace evaluation
        </Button>
      </div>
    </div>
  );
};

const TraceInput = ({ label, value, onChange, mono, testid }) => (
  <label className="block">
    <span className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</span>
    <input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testid}
      className={`mt-1 w-full h-7 px-2 text-xs rounded border border-input bg-background ${mono ? 'font-mono-num' : ''}`}
    />
  </label>
);
